import { QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { statusOf } from "@common/api";
import { session } from "@common/auth/session";
import { SessionProvider, useSession, useSignedIn } from "@jkast90/kast-common/auth";
import { createQueryClient, useCacheScopedToUser } from "@jkast90/kast-common/query-client";
import { logger, recordNavigation } from "@jkast90/kast-common/diagnostics";
import Banner from "./components/Banner";
import ErrorBoundary from "@jkast90/kast-common/error-boundary";
import Footer from "./components/Footer";
import Header from "./components/Header";
import { useAdminMode } from "./hooks/useAdminMode";
import { useTheme } from "./hooks/useTheme";
import AboutPage from "./pages/AboutPage";
import AccountPage from "./pages/AccountPage";
import BugReportPage from "./pages/BugReportPage";
import HomePage from "./pages/HomePage";

// Loaded on first visit to /admin, so public visitors don't download the
// shared Table and its styles.
const AdminPage = lazy(() => import("./pages/AdminPage"));

export const queryClient = createQueryClient({ statusOf });

function Shell() {
  const { user } = useSession();
  useCacheScopedToUser(queryClient, user?.sub ?? null);
  // A stored session still loading has a user but isn't signed in yet.
  const adminMode = useAdminMode(useSignedIn() ? user : null);
  const theme = useTheme();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const section = pathname.split("/")[1];

  // The pages leading up to a bug report, deep links and back/forward included.
  useEffect(() => {
    recordNavigation(pathname);
  }, [pathname]);

  return (
    <>
      <Header adminMode={adminMode} />
      <Banner />
      <ErrorBoundary
        key={section}
        onError={(error, info) => logger.error(`[Crash] ${error.message}`, info.componentStack ?? "")}
        onReport={(crashNote) => navigate("/bug-report", { state: { crashNote } })}
        buttonClassName="btn"
        primaryButtonClassName="btn primary"
      >
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/account" element={<AccountPage adminMode={adminMode} />} />
          <Route
            path="/admin"
            element={
              <Suspense fallback={<p className="muted">Loading…</p>}>
                <AdminPage adminMode={adminMode} />
              </Suspense>
            }
          />
          <Route path="/bug-report" element={<BugReportPage adminMode={adminMode} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ErrorBoundary>
      <Footer theme={theme} />
    </>
  );
}

export default function App() {
  return (
    <SessionProvider session={session}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Shell />
        </BrowserRouter>
      </QueryClientProvider>
    </SessionProvider>
  );
}
